import React from 'react';
import { BarChart2, MessageCircle, Gift, Trophy, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { ActivityData } from '../hooks/useStatsData';
import { CardSkeleton } from './CardSkeleton';
interface CardActivitySummaryProps {
  loading: boolean;
  data: ActivityData | null;
  timeFilter: '7days' | '30days' | 'alltime';
  onTimeFilterChange: (filter: '7days' | '30days' | 'alltime') => void;
}
export const CardActivitySummary: React.FC<CardActivitySummaryProps> = ({
  loading,
  data,
  timeFilter,
  onTimeFilterChange
}) => {
  if (loading) return <CardSkeleton data-id="element-20717" />;
  if (!data) return <div className="bg-white dark:bg-gradient-to-b dark:from-[#0f0c29] dark:to-[#302b63] rounded-lg border border-purple-200/50 dark:border-indigo-500/30 p-5 shadow-sm dark:shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm" data-id="element-20718">
        <div className="flex items-center justify-between mb-4" data-id="element-20719">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-r dark:from-indigo-300 dark:to-blue-200" data-id="element-20720">
            <BarChart2 className="text-indigo-500 dark:text-indigo-400" size={18} data-id="element-20721" />
            Activity Summary
          </h3>
        </div>
        <div className="flex flex-col items-center justify-center py-8" data-id="element-20722">
          <p className="text-gray-500 dark:text-indigo-200/70 text-sm" data-id="element-20723">
            No activity data available yet.
          </p>
        </div>
      </div>;
  // Helper function to render trend icon
  const renderTrendIcon = (trend: number) => {
    if (trend > 0) {
      return <TrendingUp size={14} className="text-green-500" data-id="element-20724" />;
    } else if (trend < 0) {
      return <TrendingDown size={14} className="text-red-500" data-id="element-20725" />;
    } else {
      return <Minus size={14} className="text-gray-400" data-id="element-20726" />;
    }
  };
  return <div className="bg-white dark:bg-gradient-to-b dark:from-[#0f0c29] dark:to-[#302b63] rounded-lg border border-purple-200/50 dark:border-indigo-500/30 p-5 shadow-sm dark:shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm relative overflow-hidden" data-id="element-20727">
      <div className="relative" data-id="element-20728">
        <div className="flex items-center justify-between mb-4" data-id="element-20729">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-r dark:from-indigo-300 dark:to-blue-200" data-id="element-20730">
            <BarChart2 className="text-indigo-500 dark:text-indigo-400" size={18} data-id="element-20731" />
            Activity Summary
          </h3>
          <div className="flex bg-indigo-50 dark:bg-indigo-900/50 backdrop-blur-sm rounded-lg overflow-hidden border border-indigo-200/50 dark:border-indigo-500/20" data-id="element-20732">
            <button className={`px-3 py-1.5 text-xs font-medium ${timeFilter === '7days' ? 'bg-indigo-500 text-white' : 'text-indigo-600 dark:text-indigo-300'}`} onClick={() => onTimeFilterChange('7days')} data-id="element-20733">
              7 days
            </button>
            <button className={`px-3 py-1.5 text-xs font-medium ${timeFilter === '30days' ? 'bg-indigo-500 text-white' : 'text-indigo-600 dark:text-indigo-300'}`} onClick={() => onTimeFilterChange('30days')} data-id="element-20734">
              30 days
            </button>
            <button className={`px-3 py-1.5 text-xs font-medium ${timeFilter === 'alltime' ? 'bg-indigo-500 text-white' : 'text-indigo-600 dark:text-indigo-300'}`} onClick={() => onTimeFilterChange('alltime')} data-id="element-20735">
              All time
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-20736">
          {/* Messages sent */}
          <div className="bg-indigo-50 dark:bg-indigo-900/30 backdrop-blur-sm rounded-lg p-3 border border-indigo-200/50 dark:border-indigo-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(79,70,229,0.1)]" data-id="element-20737">
            <div className="flex items-center justify-between mb-2" data-id="element-20738">
              <div className="flex items-center gap-1.5 text-indigo-600 dark:text-indigo-300" data-id="element-20739">
                <MessageCircle size={16} data-id="element-20740" />
                <span className="text-xs" data-id="element-20741">Messages</span>
              </div>
              <div className="flex items-center" data-id="element-20742">
                {renderTrendIcon(data.messages.trend)}
                <span className={`text-xs ml-1 ${data.messages.trend > 0 ? 'text-green-500' : data.messages.trend < 0 ? 'text-red-500' : 'text-gray-400'}`} data-id="element-20743">
                  {Math.abs(data.messages.trend)}%
                </span>
              </div>
            </div>
            <p className="font-bold text-lg text-indigo-600 dark:text-indigo-200 etched-text" data-id="element-20744">
              {data.messages[timeFilter]}
            </p>
          </div>
          {/* Gifts sent */}
          <div className="bg-pink-50 dark:bg-pink-900/30 backdrop-blur-sm rounded-lg p-3 border border-pink-200/50 dark:border-pink-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(236,72,153,0.1)]" data-id="element-20745">
            <div className="flex items-center justify-between mb-2" data-id="element-20746">
              <div className="flex items-center gap-1.5 text-pink-600 dark:text-pink-300" data-id="element-20747">
                <Gift size={16} data-id="element-20748" />
                <span className="text-xs" data-id="element-20749">Gifts</span>
              </div>
              <div className="flex items-center" data-id="element-20750">
                {renderTrendIcon(data.gifts.trend)}
                <span className={`text-xs ml-1 ${data.gifts.trend > 0 ? 'text-green-500' : data.gifts.trend < 0 ? 'text-red-500' : 'text-gray-400'}`} data-id="element-20751">
                  {Math.abs(data.gifts.trend)}%
                </span>
              </div>
            </div>
            <p className="font-bold text-lg text-pink-600 dark:text-pink-200 etched-text" data-id="element-20752">
              {data.gifts[timeFilter]}
            </p>
          </div>
          {/* Games played */}
          <div className="bg-amber-50 dark:bg-amber-900/30 backdrop-blur-sm rounded-lg p-3 border border-amber-200/50 dark:border-amber-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(251,191,36,0.1)]" data-id="element-20753">
            <div className="flex items-center justify-between mb-2" data-id="element-20754">
              <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-300" data-id="element-20755">
                <Trophy size={16} data-id="element-20756" />
                <span className="text-xs" data-id="element-20757">Games</span>
              </div>
              <div className="flex items-center" data-id="element-20758">
                {renderTrendIcon(data.games.trend)}
                <span className={`text-xs ml-1 ${data.games.trend > 0 ? 'text-green-500' : data.games.trend < 0 ? 'text-red-500' : 'text-gray-400'}`} data-id="element-20759">
                  {Math.abs(data.games.trend)}%
                </span>
              </div>
            </div>
            <p className="font-bold text-lg text-amber-600 dark:text-amber-200 etched-text" data-id="element-20760">
              {data.games[timeFilter]}
            </p>
            <p className="text-xs text-amber-500 dark:text-amber-300" data-id="element-20761">
              {data.games.won[timeFilter]} victories
            </p>
          </div>
          {/* Rituals completed */}
          <div className="bg-purple-50 dark:bg-purple-900/30 backdrop-blur-sm rounded-lg p-3 border border-purple-200/50 dark:border-purple-500/20 shadow-sm dark:shadow-[0_0_10px_rgba(168,85,247,0.1)]" data-id="element-20762">
            <div className="flex items-center justify-between mb-2" data-id="element-20763">
              <div className="flex items-center gap-1.5 text-purple-600 dark:text-purple-300" data-id="element-20764">
                <BarChart2 size={16} data-id="element-20765" />
                <span className="text-xs" data-id="element-20766">Rituals</span>
              </div>
            </div>
            <p className="font-bold text-lg text-purple-600 dark:text-purple-200 etched-text" data-id="element-20767">
              {data.rituals[timeFilter].daily + data.rituals[timeFilter].weekly + data.rituals[timeFilter].premium}
            </p>
            <div className="flex gap-2 text-xs text-purple-500 dark:text-purple-300" data-id="element-20768">
              <span data-id="element-20769">{data.rituals[timeFilter].daily} daily</span>
              <span data-id="element-20770">•</span>
              <span data-id="element-20771">{data.rituals[timeFilter].weekly} weekly</span>
              <span data-id="element-20772">•</span>
              <span data-id="element-20773">{data.rituals[timeFilter].premium} premium</span>
            </div>
          </div>
        </div>
        {/* Interaction heatmap */}
        <div className="mb-4" data-id="element-20774">
          <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-indigo-200" data-id="element-20775">
            Activity Heatmap (Last 7 Days)
          </h4>
          <div className="h-20 flex items-end gap-1" data-id="element-20776">
            {data.heatmap.map((height, i) => <div key={i} className="flex-1 flex flex-col items-center gap-1" data-id="element-20777">
                <div className="w-full bg-gradient-to-t from-indigo-500 to-purple-500 dark:from-indigo-600 dark:to-purple-600 rounded-t-sm shadow-sm dark:shadow-[0_0_5px_rgba(79,70,229,0.5)] relative overflow-hidden" style={{
              height: `${height}%`
            }} data-id="element-20778"></div>
                <span className="text-xs text-gray-500 dark:text-indigo-200/70" data-id="element-20779">
                  {['M', 'T', 'W', 'T', 'F', 'S', 'S'][i]}
                </span>
              </div>)}
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-purple-200/30 dark:border-indigo-500/20" data-id="element-20780">
          <p className="text-xs text-gray-500 dark:text-indigo-200/70" data-id="element-20781">
            Your activity helps you earn rewards, build relationships, and
            unlock exclusive content.
          </p>
        </div>
      </div>
    </div>;
};